import React from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import { Button, Input } from 'components';
import { yup } from 'utils';
import { emailRegex } from 'utils/regex';

type PayloadType = { username: string; password: string };
/* eslint-disable no-unused-vars */
type FormType = {
  onButton: (data: PayloadType) => void;
  defaultEmail?: string;
  isSubmitting?: boolean;
};
const Form: React.FC<FormType> = ({
  onButton,
  defaultEmail,
  isSubmitting = false,
}: FormType) => {
  const { t } = useTranslation('component.auth.login.form');

  const schema = yup.object().shape({
    username: yup
      .string()
      .email()
      .lowercase()
      .matches(emailRegex, t('email_validate'))
      .label('username')
      .required(),
    password: yup.string().label('password').required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: PayloadType) => {
    onButton(data);
  };

  React.useEffect(() => {
    if (defaultEmail) {
      setValue('username', defaultEmail);
    }
  }, [defaultEmail]);

  const formProps = useInputAttributes(register, errors, {
    username: t('username'),
    password: t('password'),
  });

  const fieldSurface = {
    background: 'secondary',
    borderWidth: '1px',
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 'lg',
    _hover: { background: 'rgba(255,255,255,0.05)' },
    _focus: {
      background: 'secondary',
      borderColor: 'primary',
    },
  };

  return (
    <Box w="100%">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb={5}>
          <Input
            {...formProps.username}
            isDisabled={Boolean(defaultEmail) || isSubmitting}
            {...fieldSurface}
          />
        </Box>
        <Box>
          <Input
            type="password"
            {...formProps.password}
            {...fieldSurface}
            isDisabled={isSubmitting}
          />
        </Box>
        <Button
          type="submit"
          variant="filled"
          display="block"
          title={t('login')}
          minW="100%"
          mt={8}
          size="sm"
          h={10}
          fontSize="sm"
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
        />
      </form>
    </Box>
  );
};

export default Form;
